import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useListHsnCodes } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";

interface Props {
  value: string | null | undefined;
  // Selecting a code reports the code AND its GST rate so the form can auto-fill GST.
  onChange: (code: string, gstRate: number | null) => void;
}

// 2026-05-22: HSN/SAC picker sourced from the HSN→GST master. Picking a code auto-fills
// the item's GST rate. Manage codes under Inventory → HSN / GST.
export function HsnCodeCombobox({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: codes = [] } = useListHsnCodes({ status: "Active" });

  const q = search.trim().toLowerCase();
  const filtered = q
    ? codes.filter(
        (c) => c.code.toLowerCase().includes(q) || (c.description ?? "").toLowerCase().includes(q),
      )
    : codes;

  const selected = codes.find((c) => c.code === value);
  const label = selected
    ? `${selected.code} · ${Number(selected.gstRate)}% GST`
    : value
      ? value
      : "Select HSN / SAC code…";

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", !value && "text-muted-foreground")}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search code or description…" value={search} onValueChange={setSearch} />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No code found. Add it under Inventory → HSN / GST.</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.code}
                  onSelect={() => { onChange(c.code, Number(c.gstRate)); setSearch(""); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === c.code ? "opacity-100" : "opacity-0")} />
                  <span className="flex-1 truncate">
                    <span className="font-mono">{c.code}</span>
                    {c.description ? <span className="text-muted-foreground"> · {c.description}</span> : null}
                  </span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">{Number(c.gstRate)}%</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
